import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ChallengeDto } from '@qc/shared/dto';
import { MfaType } from '@qc/shared/enums';
import { of, EMPTY, Observable } from 'rxjs';
import { map, exhaustMap, catchError, tap, withLatestFrom, switchMap } from 'rxjs/operators';
import { Action, Store } from '@ngrx/store';

import * as AuthActions from './auth.actions';
import {
    AuthService,
    AuthChallengeService,
    UserService,
    NotificationService,
    TokenStorageService,
    ChallengeStorageService,
    ChallengeProcessorService,
} from '@/app/core/services';
import { selectAuthState, selectRefreshToken } from './auth.selectors';

interface ActionWithStoragePayload extends Action {
    accessToken?: string | null;
    refreshToken?: string | null;
    authChallengeToken?: string | null;
    challenge?: ChallengeDto;
    otpAuthUri?: string | null;
    secretKey?: string | null;
    rememberMe?: boolean;
}

@Injectable()
export class AuthEffects {
    readonly #actions$ = inject(Actions);
    readonly #authService = inject(AuthService);
    readonly #authChallengeService = inject(AuthChallengeService);
    readonly #userService = inject(UserService);
    readonly #notificationService = inject(NotificationService);
    readonly #tokenStorage = inject(TokenStorageService);
    readonly #challengeStorage = inject(ChallengeStorageService);
    readonly #challengeProcessor = inject(ChallengeProcessorService);
    readonly #router = inject(Router);
    readonly #store = inject(Store);

    private handleAuthError(
        error: any,
        failureActionCreator: (props: { error: string }) => Action,
        notificationTitle: string,
        defaultErrorMessage: string,
        customErrorHandler?: (error: HttpErrorResponse) => void,
    ): Observable<Action> {
        let errorMessage = defaultErrorMessage;
        if (error instanceof HttpErrorResponse) {
            errorMessage = error.error?.message || defaultErrorMessage;
            if (customErrorHandler) {
                customErrorHandler(error);
            }
        } else if (error instanceof Error) {
            errorMessage = error.message;
        } else if (typeof error === 'string') {
            errorMessage = error;
        }
        try {
            this.#notificationService.showError(notificationTitle, errorMessage);
        } catch (e) {
            console.error('Failed to show notification', e);
        }
        return of(failureActionCreator({ error: errorMessage }));
    }

    login$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.login),
            exhaustMap((action) =>
                this.#authService.login(action.credentials).pipe(
                    map(({ data: { accessToken, refreshToken, authChallengeToken, challenge } }) =>
                        AuthActions.loginSuccess({
                            accessToken,
                            refreshToken,
                            authChallengeToken,
                            challenge,
                            rememberMe: action.credentials.rememberMe,
                        }),
                    ),
                    catchError((error: HttpErrorResponse) =>
                        this.handleAuthError(
                            error,
                            AuthActions.loginFailure,
                            'Login Failed',
                            'Invalid email or password',
                        ),
                    ),
                ),
            ),
        ),
    );

    loginSuccess$ = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(AuthActions.loginSuccess),
                switchMap((result) => this.#challengeProcessor.processChallenge(result)),
            ),
        { dispatch: false },
    );

    initiateChallengeVerification$ = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(AuthActions.initiateChallengeVerification),
                switchMap((result) => this.#challengeProcessor.processChallenge(result)),
            ),
        { dispatch: false },
    );

    initiateMfaSetup$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.initiateMfaSetup),
            exhaustMap(({ mfaType, identifier }) => {
                if (mfaType === 'TOTP') {
                    return this.#authChallengeService
                        .initMfaMethod({
                            type: mfaType as MfaType,
                            identifier,
                        })
                        .pipe(
                            map(({ data }) =>
                                AuthActions.initiateMfaSetupSuccess({
                                    authChallengeToken: data.authChallengeToken!,
                                    challenge: data.challenge!,
                                    otpAuthUri: data.otpAuthUri,
                                    secretKey: data.secretKey,
                                }),
                            ),
                            catchError((error: HttpErrorResponse) =>
                                this.handleAuthError(
                                    error,
                                    AuthActions.initiateMfaSetupFailure,
                                    'MFA Setup Failed',
                                    'Could not set up MFA',
                                    (httpError) => {
                                        if (httpError.status === 401) {
                                            this.#router.navigate(['/auth/login']);
                                        }
                                    },
                                ),
                            ),
                        );
                }

                return this.#challengeProcessor
                    .submitChallenge(this.#authChallengeService.initMfaMethod.bind(this.#authChallengeService), {
                        type: mfaType as MfaType,
                        identifier,
                    })
                    .pipe(
                        map((result) =>
                            AuthActions.initiateMfaSetupSuccess({
                                authChallengeToken: result.authChallengeToken!,
                                challenge: result.challenge!,
                                otpAuthUri: result.otpAuthUri,
                                secretKey: result.secretKey,
                            }),
                        ),
                        catchError((error: HttpErrorResponse) =>
                            this.handleAuthError(
                                error,
                                AuthActions.initiateMfaSetupFailure,
                                'MFA Setup Failed',
                                'Could not set up MFA',
                                (httpError) => {
                                    if (httpError.status === 401) {
                                        this.#router.navigate(['/auth/login']);
                                    }
                                },
                            ),
                        ),
                    );
            }),
        ),
    );

    verifyMfaCode$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.verifyMfaCode),
            exhaustMap((action) =>
                this.#challengeProcessor
                    .submitChallenge(this.#authChallengeService.verifyOtp.bind(this.#authChallengeService), {
                        code: action.code,
                    })
                    .pipe(
                        map((result) =>
                            AuthActions.verifyMfaCodeSuccess({
                                authChallengeToken: result.authChallengeToken!,
                                challenge: result.challenge,
                            }),
                        ),
                        catchError((error: HttpErrorResponse) =>
                            this.handleAuthError(
                                error,
                                AuthActions.verifyMfaCodeFailure,
                                'MFA Verification Failed',
                                'Invalid verification code',
                                (httpError) => {
                                    if (httpError.status === 401) {
                                        this.#router.navigate(['/auth/login']);
                                    }
                                },
                            ),
                        ),
                    ),
            ),
        ),
    );

    verifyMfaCodeSuccess$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.verifyMfaCodeSuccess),
            switchMap((result) => {
                if (!result.challenge) {
                    return of(AuthActions.completeLogin());
                }

                if (result.challenge && result.authChallengeToken) {
                    this.#challengeProcessor.processChallenge(result);
                }

                return EMPTY;
            }),
        ),
    );

    changeMfaMethod$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.changeMfaMethod),
            exhaustMap((action) =>
                this.#challengeProcessor
                    .submitChallenge(this.#authChallengeService.changeMfa.bind(this.#authChallengeService), {
                        mfaMethodId: action.mfaMethodId,
                    })
                    .pipe(
                        map((result) =>
                            AuthActions.changeMfaMethodSuccess({
                                authChallengeToken: result.authChallengeToken!,
                                challenge: result.challenge,
                            }),
                        ),
                        catchError((error: HttpErrorResponse) =>
                            this.handleAuthError(
                                error,
                                AuthActions.changeMfaMethodFailure,
                                'MFA Method Change Failed',
                                'Could not change MFA method',
                                (httpError) => {
                                    if (httpError.status === 401) {
                                        this.#router.navigate(['/auth/login']);
                                    }
                                },
                            ),
                        ),
                    ),
            ),
        ),
    );

    changeMfaMethodSuccess$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.changeMfaMethodSuccess),
            switchMap((result) => {
                if (!result.challenge) {
                    return of(AuthActions.completeLogin());
                }

                if (result.challenge && result.authChallengeToken) {
                    this.#challengeProcessor.processChallenge(result);
                }

                return EMPTY;
            }),
        ),
    );

    verifyDevice$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.verifyDevice),
            exhaustMap((action) =>
                this.#challengeProcessor
                    .submitChallenge(this.#authChallengeService.verifyDevice.bind(this.#authChallengeService), {
                        code: action.code,
                    })
                    .pipe(
                        map((result) =>
                            AuthActions.verifyDeviceSuccess({
                                authChallengeToken: result.authChallengeToken!,
                                challenge: result.challenge,
                            }),
                        ),
                        catchError((error: HttpErrorResponse) =>
                            this.handleAuthError(
                                error,
                                AuthActions.verifyDeviceFailure,
                                'Device Verification Failed',
                                'Invalid verification code',
                                (httpError) => {
                                    if (httpError.status === 401) {
                                        this.#router.navigate(['/auth/login']);
                                    }
                                },
                            ),
                        ),
                    ),
            ),
        ),
    );

    verifyDeviceSuccess$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.verifyDeviceSuccess),
            switchMap((result) => {
                if (!result.challenge) {
                    return of(AuthActions.completeLogin());
                }

                if (result.challenge && result.authChallengeToken) {
                    this.#challengeProcessor.processChallenge(result);
                }

                return EMPTY;
            }),
        ),
    );

    completeLogin$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.completeLogin),
            exhaustMap(() => {
                const token = this.#challengeStorage.getAuthChallengeToken();
                if (!token) {
                    return of(
                        AuthActions.completeLoginFailure({
                            error: 'No challenge token found',
                        }),
                    );
                }
                return this.#authService.completeLogin().pipe(
                    map(({ data: { accessToken, refreshToken, authChallengeToken, challenge } }) =>
                        AuthActions.completeLoginSuccess({
                            accessToken,
                            refreshToken,
                            authChallengeToken,
                            challenge,
                        }),
                    ),
                    catchError((error: HttpErrorResponse) =>
                        this.handleAuthError(
                            error,
                            AuthActions.completeLoginFailure,
                            'Login Failed',
                            'Could not complete authentication',
                        ),
                    ),
                );
            }),
        ),
    );

    completeLoginSuccess$ = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(AuthActions.completeLoginSuccess),
                switchMap((result) => this.#challengeProcessor.processChallenge(result)),
            ),
        { dispatch: false },
    );

    loadUserProfile$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.loadUserProfile),
            exhaustMap(() =>
                this.#userService.getProfile().pipe(
                    map(({ data }) => AuthActions.loadUserProfileSuccess({ user: data })),
                    catchError((error: HttpErrorResponse) =>
                        this.handleAuthError(
                            error,
                            AuthActions.loadUserProfileFailure,
                            'User Profile Error',
                            'Could not load user profile',
                        ),
                    ),
                ),
            ),
        ),
    );

    refreshToken$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.refreshToken),
            withLatestFrom(this.#store.select(selectRefreshToken)),
            switchMap(([_, refreshToken]) => {
                if (!refreshToken) {
                    console.error('No refresh token found in store when attempting token refresh');
                    return of(AuthActions.refreshTokenFailure({ error: 'No refresh token available' }));
                }
                ;
                return this.#authService.refreshToken(refreshToken).pipe(
                    map(({ data: { accessToken, refreshToken } }) =>
                        AuthActions.refreshTokenSuccess({
                            accessToken,
                            refreshToken,
                        }),
                    ),
                    catchError((error: HttpErrorResponse) => {
                        console.error('Token refresh failed:', error);
                        return this.handleAuthError(
                            error,
                            AuthActions.refreshTokenFailure,
                            'Token Refresh Failed',
                            'Could not refresh authentication',
                        );
                    }),
                );
            }),
        ),
    );

    refreshTokenSuccess$ = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(AuthActions.refreshTokenSuccess),
                tap(({ accessToken, refreshToken }) => {
                    if (accessToken && refreshToken) {
                        // Preserve rememberMe setting from original login
                        const rememberMe = localStorage.getItem('rememberMe') === 'true' ||
                                         sessionStorage.getItem('rememberMe') === 'true';
                        this.#tokenStorage.saveTokens(accessToken, refreshToken, rememberMe);
                    }
                }),
            ),
        { dispatch: false },
    );

    logout$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.logout),
            exhaustMap(() =>
                this.#authService.logout().pipe(
                    map(() => AuthActions.logoutSuccess()),
                    catchError(() => of(AuthActions.logoutSuccess())),
                ),
            ),
        ),
    );

    logoutSuccess$ = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(AuthActions.logoutSuccess),
                tap(() => {
                    this.#tokenStorage.clearTokens();
                    this.#challengeStorage.clearChallenge();
                    this.#router.navigate(['/auth/login']);
                }),
            ),
        { dispatch: false },
    );

    initAuthFromStorage$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.initAuthFromStorage),
            exhaustMap(() => {
                const accessToken = this.#tokenStorage.getAccessToken();
                const refreshToken = this.#tokenStorage.getRefreshToken();
                const challenge = this.#challengeStorage.getChallengeFromStorage();

                if (accessToken && refreshToken) {
                    return of(AuthActions.loadUserProfile());
                } else if (challenge?.authChallengeToken) {
                    const challengeDto = challenge.challenge;

                    if (challengeDto) {
                        return of(
                            AuthActions.restoreChallengeState({
                                authChallengeToken: challenge.authChallengeToken,
                                challenge: challengeDto,
                            }),
                        );
                    }
                }

                return EMPTY;
            }),
        ),
    );

    restoreChallengeState$ = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(AuthActions.restoreChallengeState),
                tap(({ challenge }) => {
                    if (challenge) {
                        this.#challengeProcessor.routeToChallenge(challenge.type);
                    }
                }),
            ),
        { dispatch: false },
    );

    saveAuthDataToStorage$ = createEffect(
        () =>
            this.#actions$.pipe(
                ofType<ActionWithStoragePayload>(
                    AuthActions.loginSuccess,
                    AuthActions.completeLoginSuccess,
                    AuthActions.changePasswordSuccess,
                    AuthActions.verifyMfaCodeSuccess,
                    AuthActions.verifyDeviceSuccess,
                ),
                tap(({ accessToken, refreshToken, authChallengeToken, challenge, rememberMe }) => {
                    if (accessToken && refreshToken) {
                        this.#challengeStorage.clearChallenge();
                        // Check if rememberMe was set during login (for subsequent auth steps)
                        const savedRememberMe = localStorage.getItem('rememberMe') === 'true' ||
                                               sessionStorage.getItem('rememberMe') === 'true';
                        this.#tokenStorage.saveTokens(accessToken, refreshToken, rememberMe ?? savedRememberMe);
                    } else if (authChallengeToken && challenge) {
                        this.#challengeStorage.saveChallenge({
                            authChallengeToken,
                            challenge,
                            challengeType: challenge.type,
                            challengeReason: challenge.reason,
                            challengeDelivery: challenge.delivery,
                            details: challenge.details,
                        });
                    }
                }),
            ),
        { dispatch: false },
    );

    clearChallenge$ = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(AuthActions.clearChallenge),
                tap(() => {
                    this.#challengeStorage.clearChallenge();
                }),
            ),
        { dispatch: false },
    );

    changePassword$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.changePassword),
            exhaustMap((action) =>
                this.#challengeProcessor
                    .submitChallenge(this.#authChallengeService.changePassword.bind(this.#authChallengeService), {
                        newPassword: action.newPassword,
                        confirmPassword: action.confirmPassword,
                    })
                    .pipe(
                        map((result) =>
                            AuthActions.changePasswordSuccess({
                                authChallengeToken: result.authChallengeToken || null,
                                challenge: result.challenge,
                            }),
                        ),
                        catchError((error: HttpErrorResponse) =>
                            this.handleAuthError(
                                error,
                                AuthActions.changePasswordFailure,
                                'Password Change Failed',
                                'Could not change password',
                            ),
                        ),
                    ),
            ),
        ),
    );

    changePasswordSuccess$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.changePasswordSuccess),
            switchMap((result) => {
                if (!result.challenge) {
                    return of(AuthActions.completeLogin());
                }

                if (result.challenge && result.authChallengeToken) {
                    this.#challengeProcessor.processChallenge(result);
                }

                return EMPTY;
            }),
        ),
    );

    skipPasswordChange$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.skipPasswordChange),
            map(() => AuthActions.completeLogin()),
        ),
    );

    updateAuthChallengeToken$ = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(AuthActions.updateAuthChallengeToken),
                withLatestFrom(this.#store.select(selectAuthState)),
                tap(([{ authChallengeToken }]) => {
                    this.#challengeProcessor.processChallenge({ authChallengeToken }, true);
                }),
            ),
        { dispatch: false },
    );

    resendOtp$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(AuthActions.resendOtp),
            exhaustMap(() =>
                this.#challengeProcessor
                    .submitChallenge(this.#authChallengeService.resendOtp.bind(this.#authChallengeService), {})
                    .pipe(
                        map((result) =>
                            AuthActions.resendOtpSuccess({
                                authChallengeToken: result.authChallengeToken!,
                                challenge: result.challenge,
                            }),
                        ),
                        catchError((error: HttpErrorResponse) =>
                            this.handleAuthError(
                                error,
                                AuthActions.resendOtpFailure,
                                'Resend OTP Failed',
                                'Could not resend verification code. Please wait before trying again.',
                                (httpError) => {
                                    if (httpError.status === 401) {
                                        this.#router.navigate(['/auth/login']);
                                    }
                                },
                            ),
                        ),
                    ),
            ),
        ),
    );
}
